import { DecisionNode, NodeOption, Trade, Urgency } from '../../../core/models/decision-tree.model';

/**
 * Pure tree-traversal helpers — no dependencies on Angular or storage.
 * Lifted out of the store so we can unit-test the 3 example flows from the
 * spec without spinning up a TestBed.
 */

export interface AdvanceResult {
  /** Trade-local key of the next node to render, or null if we hit the end. */
  nextNodeKey: string | null;
  /** autoSetUrgency override the chosen option carries (if any). */
  autoUrgency: Urgency | null;
}

export class UnknownNodeError extends Error {
  constructor(key: string) {
    super(`Unknown node '${key}' in trade.`);
  }
}

export class UnknownOptionError extends Error {
  constructor(key: string, value: string) {
    super(`Unknown option value '${value}' for node '${key}'.`);
  }
}

/** Look up a node by trade-local key. Throws on miss to surface bad spec data fast. */
export function nodeAt(trade: Trade, key: string): DecisionNode {
  const node = trade.nodes[key];
  if (!node) throw new UnknownNodeError(key);
  return node;
}

export function findOption(node: DecisionNode, value: string): NodeOption {
  const opt = (node.options ?? []).find((o) => o.value === value);
  if (!opt) throw new UnknownOptionError(node.id, value);
  return opt;
}

/**
 * Advance from `currentNodeKey` after the user selects `optionValue`.
 * Per-option `next` wins; otherwise inherit the node-level `next` (used by
 * inlined globals like `urgency`/`duration` whose options share one transition).
 *
 * autoSetUrgency may also be set on the node itself (e.g. info_step) — that's
 * resolved separately by {@link advanceFromInfoStep}.
 */
export function advanceFromSingleSelect(
  trade: Trade,
  currentNodeKey: string,
  optionValue: string,
): AdvanceResult {
  const node = nodeAt(trade, currentNodeKey);
  if (node.type !== 'single_select') {
    throw new Error(`Node '${currentNodeKey}' is not single_select.`);
  }
  const opt = findOption(node, optionValue);
  const next = opt.next ?? node.next ?? null;
  return {
    nextNodeKey: next,
    autoUrgency: parseUrgency(opt.autoSetUrgency),
  };
}

/** Pass-through info_step → returns its `next` and any node-level autoSetUrgency. */
export function advanceFromInfoStep(trade: Trade, currentNodeKey: string): AdvanceResult {
  const node = nodeAt(trade, currentNodeKey);
  if (node.type !== 'info_step') {
    throw new Error(`Node '${currentNodeKey}' is not info_step.`);
  }
  return {
    nextNodeKey: node.next ?? null,
    autoUrgency: parseUrgency(node.autoSetUrgency),
  };
}

/**
 * If autoSetUrgency has fired on the path, the urgency_ref step is a no-op for
 * the customer — we skip it. Returns the canonical (post-urgency) key, walking
 * past info_steps that have no further interaction.
 */
export function skipUrgencyIfAutoSet(
  trade: Trade,
  fromKey: string,
  autoUrgency: Urgency | null,
): string {
  if (!autoUrgency) return fromKey;

  let cursor: string | null = fromKey;
  let safety = 0;
  while (cursor && safety++ < 50) {
    const node: DecisionNode | undefined = trade.nodes[cursor];
    if (!node) return cursor;
    // The urgency component's canonical id is "urgency". When auto-set, jump past it.
    if (node.id === 'urgency') {
      cursor = node.next ?? null;
      continue;
    }
    return cursor;
  }
  return fromKey;
}

function parseUrgency(value: string | null | undefined): Urgency | null {
  switch (value) {
    case 'notfall':
    case 'heute':
    case 'diese_woche':
    case 'flexibel':
      return value;
    default:
      return null;
  }
}

/**
 * Render-time helper: which keys index into the answers map for the visible
 * progress bar / breadcrumb? Walks the tree following the answers, like the
 * server's own validator/output builder.
 */
export function walkPath(
  trade: Trade,
  answers: Readonly<Record<string, string>>,
): string[] {
  const visited: string[] = [];
  let cursor: string | null = trade.rootNodeId;
  let safety = 0;

  while (cursor && safety++ < 100) {
    const node: DecisionNode | undefined = trade.nodes[cursor];
    if (!node) break;
    visited.push(cursor);
    if (node.type === 'summary_and_extras') break;
    if (node.type === 'info_step') {
      cursor = node.next ?? null;
      continue;
    }
    // single_select
    const value = answers[node.id];
    if (!value) break;
    const opt = (node.options ?? []).find((o: NodeOption) => o.value === value);
    if (!opt) break;
    cursor = opt.next ?? node.next ?? null;
  }
  return visited;
}
